import { glideLoader, IMAGE_CONFIG } from '@/config/image';
import type { GlideFit, GlideFormat } from '@/types/image';

export function useImageLoader() {
  function getOptimalWidths(targetWidth?: number, maxWidth?: number): number[] {
    const allWidths = [
      ...IMAGE_CONFIG.imageSizes,
      ...IMAGE_CONFIG.deviceSizes,
    ].sort((a, b) => a - b);

    if (!targetWidth) {
      return IMAGE_CONFIG.deviceSizes;
    }

    const filteredWidths = allWidths.filter((width) => {
      if (maxWidth) {
        return width <= maxWidth;
      }
      return width <= targetWidth * 2;
    });

    if (!filteredWidths.includes(targetWidth)) {
      filteredWidths.push(targetWidth);
      filteredWidths.sort((a, b) => a - b);
    }

    return filteredWidths;
  }

  function generateSrcSet(
    src: string,
    widths: number[],
    aspectRatio?: number,
    quality?: number,
    fit?: GlideFit,
    format?: GlideFormat,
  ): string {
    return widths
      .map((width) => {
        const height = aspectRatio
          ? Math.round(width / aspectRatio)
          : undefined;
        const url = glideLoader({
          src,
          width,
          height,
          quality: quality || IMAGE_CONFIG.defaultQuality,
          fit: fit || 'cover',
          format: format || IMAGE_CONFIG.defaultFormat,
        });
        return `${url} ${width}w`;
      })
      .join(', ');
  }

  function buildImageUrl(
    src: string,
    width?: number,
    height?: number,
    quality?: number,
    fit?: GlideFit,
    format?: GlideFormat,
  ): string {
    return glideLoader({
      src,
      width,
      height,
      quality: quality || IMAGE_CONFIG.defaultQuality,
      fit: fit || 'cover',
      format: format || IMAGE_CONFIG.defaultFormat,
    });
  }

  function getAspectRatio(width?: number, height?: number): number | undefined {
    if (width && height) {
      return width / height;
    }
    return undefined;
  }

  function isAbsoluteUrl(src: string): boolean {
    return src.startsWith('http://') || src.startsWith('https://');
  }

  return {
    getOptimalWidths,
    generateSrcSet,
    buildImageUrl,
    getAspectRatio,
    isAbsoluteUrl,
  };
}
